/**
 * SmartVideo — Lazy load + memory release for HTMLVideoElement.
 *
 * Behavior:
 *  - Initially keeps `data-src` only; defers network until enter viewport.
 *  - Enter viewport => set src + load() + (autoplay && muted) play().
 *  - Leave viewport => pause() immediately, then after RELEASE_DELAY ms
 *    removeAttribute('src') + video.load() to release media pipeline / decoder.
 *
 * Markup contract:
 *   <video data-smart-video data-src="https://..."
 *          data-autoplay="true|false"
 *          data-release-delay="3000"
 *          poster="..." muted loop playsinline preload="none"></video>
 *
 * Public API: window.SmartVideo.init(root?), .attach(video), .release(video), .hydrate(video)
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return;
  }

  var DEFAULT_RELEASE_DELAY = 3000;
  var ATTR_BOUND = 'data-smart-bound';

  // video -> { timer, io }
  var states = new WeakMap();

  function getDelay(video) {
    var v = parseInt(video.getAttribute('data-release-delay'), 10);
    return isFinite(v) && v >= 0 ? v : DEFAULT_RELEASE_DELAY;
  }

  function clearReleaseTimer(video) {
    var s = states.get(video);
    if (s && s.timer) {
      clearTimeout(s.timer);
      s.timer = null;
    }
  }

  function hydrate(video) {
    var src = video.getAttribute('data-src');
    if (src && !video.getAttribute('src')) {
      video.setAttribute('src', src);
      try { video.load(); } catch (e) {}
    }
    var autoplay = video.getAttribute('data-autoplay') !== 'false';
    if (autoplay && video.paused) {
      // muted required by mobile autoplay policy
      if (!video.hasAttribute('muted')) video.muted = true;
      // wait one frame so layout/decoder settles
      requestAnimationFrame(function () {
        var p = video.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      });
    }
  }

  function release(video) {
    try {
      video.pause();
      // Remove direct src
      if (video.hasAttribute('src')) {
        video.removeAttribute('src');
      }
      // Remove any <source> children but keep them removable for re-hydrate
      var sources = video.querySelectorAll('source');
      for (var i = 0; i < sources.length; i++) {
        var s = sources[i];
        if (!s.dataset.src && s.src) s.dataset.src = s.src;
        s.removeAttribute('src');
      }
      // Force browser to drop media pipeline / decoder buffers
      video.load();
    } catch (e) {}
  }

  function attach(video) {
    if (!video || video.getAttribute(ATTR_BOUND) === '1') return;

    // Migrate any existing src to data-src for true lazy behavior,
    // unless explicitly opted out via data-lazy="false".
    if (video.hasAttribute('src') && video.getAttribute('data-lazy') !== 'false') {
      if (!video.getAttribute('data-src')) {
        video.setAttribute('data-src', video.getAttribute('src'));
      }
      video.removeAttribute('src');
    }
    if (!video.hasAttribute('preload')) {
      video.setAttribute('preload', 'none');
    }

    video.setAttribute(ATTR_BOUND, '1');

    var io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (entry.isIntersecting) {
            clearReleaseTimer(video);
            hydrate(video);
          } else {
            // immediate pause
            try { video.pause(); } catch (e) {}
            var s = states.get(video);
            if (s && !s.timer) {
              var delay = getDelay(video);
              s.timer = setTimeout(function () {
                release(video);
                var st = states.get(video);
                if (st) st.timer = null;
              }, delay);
            }
          }
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    states.set(video, { timer: null, io: io });
    io.observe(video);
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('video[data-smart-video]:not([' + ATTR_BOUND + '="1"])');
    for (var i = 0; i < nodes.length; i++) attach(nodes[i]);
  }

  window.SmartVideo = {
    init: init,
    attach: attach,
    hydrate: hydrate,
    release: release,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Shopify section editor compatibility
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });

  // Observe DOM mutations for videos injected dynamically (e.g. deferred-media,
  // <template> clones, AJAX loaded sections).
  if ('MutationObserver' in window) {
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (!m.addedNodes) continue;
        for (var j = 0; j < m.addedNodes.length; j++) {
          var node = m.addedNodes[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('video[data-smart-video]')) {
            attach(node);
          } else if (node.querySelectorAll) {
            init(node);
          }
        }
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
